import Image from 'next/image';

import { Box, Divider, Heading, Text, useToken } from '@chakra-ui/react';

import { SanityImage } from '~/components/sanity-image';
import { TemplateButton } from '~/components/template-button';
import { trackEvent } from '~/features/analytics/track-event';
import { SanityImageType } from '~/features/page-template/models/image';
import { TemplateButton as TemplateButtonType } from '~/features/page-template/models/template-button';

import { EnterpriseRelatedCompany } from '../models/shared-models';

export function EnterpriseResourceHeroWithLargeImage({
  title,
  description,
  resourceTypeTitle,
  startDate,
  startTime,
  relatedCompanies,
  fileUrl,
  image,
  primaryCta,
}: {
  title: string;
  description: string;
  resourceTypeTitle: string;
  startDate?: string | null;
  startTime?: string;
  relatedCompanies: EnterpriseRelatedCompany[] | null;
  fileUrl?: string;
  image: SanityImageType;
  primaryCta: TemplateButtonType;
}) {
  const [containerWidth] = useToken('sizes', ['content-max-width']);

  function handlePrimaryCtaClick() {
    if (primaryCta.buttonAction === 'Coded Action' && fileUrl) {
      window.open(fileUrl, '_blank');
    }

    trackEvent({
      name: 'Enterprise Resource CTA Clicked',
      type: 'click',
      label: primaryCta.buttonLabel,
    });
  }

  return (
    <Box
      backgroundColor='blue.800'
      columnGap={{ base: '0px', lg: '32px' }}
      display='flex'
      flexWrap={{ base: 'wrap', lg: 'nowrap' }}
      justifyContent='center'
      padding={{ base: '0px 16px 0px 16px', md: '0px 124px 0px 124px', lg: '0px 0px 0px 48px', xl: '0px 0px 0px 0px' }}
    >
      <Box
        marginBottom={{ base: 'unset', md: '64px', lg: '80px' }}
        marginLeft='auto'
        marginTop={{ base: '40px', md: '64px', lg: '80px' }}
        maxW={{ base: '100%', xl: `calc(${containerWidth} / 2 - 32px)` }}
        width={{ base: '100%', lg: '50%' }}
      >
        <Text color='accent.lime' fontSize='16px' fontWeight='600'>
          {resourceTypeTitle}
        </Text>

        <Box display='flex' marginTop='24px'>
          <Image
            alt='Udacity Accenture logo'
            height={22}
            priority={true}
            src='/images/logos/udacity-accenture.svg'
            width={125}
            style={{
              height: 'auto',
              width: 'auto',
            }}
          />
          {relatedCompanies?.map((relatedCompany) => (
            <Box key={relatedCompany._id} display='flex' height='30px'>
              <Divider borderColor='white' marginX='20px' opacity={1} orientation='vertical' />
              <SanityImage image={relatedCompany.lofiLogoLight} style={{ width: 'auto' }} />
            </Box>
          ))}
        </Box>

        <Divider borderColor='accent.sky.500' margin='0px' marginY='40px' opacity={1} />

        <Heading as='h1' color='white' size='marketing-5xl'>
          {title}
        </Heading>

        {startDate && startTime && (
          <Box alignItems='center' display='flex' marginTop='24px'>
            <Text color='accent.lime'>{startDate}</Text>
            <Divider borderColor='accent.sky.500' height='16px' marginX='8px' opacity={1} orientation='vertical' />
            <Text color='accent.lime'>{startTime}</Text>
          </Box>
        )}

        <Text color='white' marginTop='24px'>
          {description}
        </Text>

        <Box marginTop='24px'>
          <TemplateButton {...primaryCta} openInNewTab={true} withArrowIcon onClick={handlePrimaryCtaClick} />
        </Box>
      </Box>

      <Box
        height={{ base: '340px', lg: 'auto' }}
        marginTop={{ base: '40px', lg: 'unset' }}
        position='relative'
        width={{ base: '100%', lg: '50%' }}
      >
        <SanityImage
          image={image}
          loading='eager'
          sizes='(min-width: 992px) 50vw, 100vw'
          style={{
            objectFit: 'contain',
            objectPosition: 'center center',
          }}
          fill
          priority
        />
      </Box>
    </Box>
  );
}
